import { XMarkIcon } from '@heroicons/react/20/solid';
import { CustomerFacetState, hasActiveFacets } from './customerFacets';

export interface CustomerActiveFiltersProps {
  facets: CustomerFacetState;
  onClearHierarchy: () => void;
  onRemoveChannel: (value: string) => void;
  onClearAll: () => void;
}

const hierarchyChipLabel = (facets: CustomerFacetState): string | null => {
  switch (facets.hierarchy.kind) {
    case 'group':
      return facets.hierarchy.groupName;
    case 'subNode':
      return `${facets.hierarchy.groupName} › ${facets.hierarchy.subNodeName}`;
    case 'independents':
      return 'Independents';
    case 'unassigned':
      return 'Unassigned';
    default:
      return null;
  }
};

function FilterChip({
  label,
  onRemove,
}: {
  label: string;
  onRemove: () => void;
}) {
  return (
    <span className='inline-flex items-center gap-1.5 rounded-lg bg-blue-50 px-2.5 py-1 text-sm font-medium text-primary'>
      {label}
      <button
        type='button'
        aria-label={`Remove ${label} filter`}
        onClick={onRemove}
        className='rounded hover:text-blue-800'
      >
        <XMarkIcon className='h-4 w-4' />
      </button>
    </span>
  );
}

export function CustomerActiveFilters({
  facets,
  onClearHierarchy,
  onRemoveChannel,
  onClearAll,
}: CustomerActiveFiltersProps) {
  if (!hasActiveFacets(facets)) return null;

  const hierarchyLabel = hierarchyChipLabel(facets);

  return (
    <div className='flex flex-wrap items-center gap-2 font-inter'>
      {hierarchyLabel && (
        <FilterChip label={hierarchyLabel} onRemove={onClearHierarchy} />
      )}
      {[...facets.channels].map((channel) => (
        <FilterChip
          key={channel}
          label={`Channel: ${channel}`}
          onRemove={() => onRemoveChannel(channel)}
        />
      ))}
      <button
        type='button'
        onClick={onClearAll}
        className='ml-1 text-sm font-medium text-gray-500 hover:text-gray-900'
      >
        Clear all
      </button>
    </div>
  );
}
